import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from 'components/StyledButton/StyledButton';

import CustomDialog from '../../components/CustomDialog/CustomDialog';
import { Props as CustomInputProps } from '../../components/CustomInput/CustomInput';
import Material, { EmptyMaterial } from '../../types/Material';
import api from '../../utils/api';
import snackbar from '../../utils/snackbar';
import Form from './components/Form/Form';

/*
 * This dialog contains the material details form.
 * It is used both to modify existing materials and to create new ones.
 */

interface Props {
    open: boolean;
    onClose: (materialId?: number) => void;
    materialId: number;
}

const MaterialDetailsDialog = (props: Props) => {
    const { open, onClose, materialId } = props;

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [materialData, setMaterialData] = useState<Material>(EmptyMaterial);

    const loadData = useCallback(() => {
        // fetch material data
        setIsLoading(true);
        api.request('/materials/' + materialId).then((res) => {
            setMaterialData(res);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [materialId]);

    const handleAttributeChange = useCallback((attribute: string, value: any) => {
        setMaterialData(currentData => {
            return { ...currentData, [attribute]: value } as Material;
        });
    }, []);

    const handleSubmit = useCallback(() => {
        // called on form submit, send the data to the API
        setIsLoading(true);

        api.request('/materials/' + (materialId === 0 ? '' : materialId), (materialId === 0 ? 'POST' : 'PUT'), materialData).then((res) => {
            const message = (materialId === 0) ? t('dialogs.material_details.new_material_saved') : t('dialogs.material_details.material_saved');
            snackbar.success(message);

            onClose(res.id);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [materialData, materialId, onClose, t]);

    const handleKeyDown = useCallback((e) => {
        // on enter key press, submit the form
        if (e.keyCode === 13 && !isLoading) {
            handleSubmit();
        }
    }, [handleSubmit, isLoading]);

    useEffect(() => {
        // call loadData if materialId !== 0, so the material already exists
        if (!open) return;
        setMaterialData(EmptyMaterial);

        if (materialId !== 0) {
            loadData();
        }
    }, [materialId, open, loadData]);

    const baseInputProps: Omit<CustomInputProps, 'name'> = {
        data: materialData,
        disabled: isLoading,
        partialTranslationKey: 'models.material.attributes',
        handleAttributeChange,
        handleKeyDown,
    };

    return (
        <>
            <CustomDialog onClose={() => onClose()} open={open} fullWidth>
                <DialogTitle className='dialog-title-text-ellipsis'>
                    {t('dialogs.material_details.title')}
                </DialogTitle>

                <DialogContent dividers>
                    <Form baseInputProps={baseInputProps} isLoading={isLoading} />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => onClose()} color='secondary' disabled={isLoading} >
                        {t('dialogs.generic.close')}
                    </Button>

                    <Button onClick={handleSubmit} color='primary' disabled={isLoading} >
                        {t('dialogs.generic.save')}
                    </Button>
                </DialogActions>
            </CustomDialog>
        </>
    );
};

export default MaterialDetailsDialog;
